/*
 * Collection filter sidebar — DESIGN ONLY (Figma 1217:21096).
 *
 * Drives the visual behaviour of the bespoke filter panel so it *feels* real:
 *   - open / close the sidebar (toggle button lives in the collection toolbar,
 *     a separate section, so the toggle is handled by a delegated listener)
 *   - check / select options, pick colours, choose a sort option
 *   - keep the "Applied filters" chips in sync with the current selection
 *   - clear all, or remove a single chip
 *
 * It deliberately does NOT filter the product grid. A custom app will read the
 * selected inputs (stable name / value / data-filter-* hooks) and apply the
 * real filtering later. Keep this purely presentational.
 */

const OPEN = 'open';
const CLOSED = 'closed';

/** Resolve the current open state. Filters are CLOSED by default on every
 *  breakpoint — only an explicit 'open' state counts as open. */
function isOpen() {
  return document.body.dataset.curaFilters === OPEN;
}

function setState(open) {
  document.body.dataset.curaFilters = open ? OPEN : CLOSED;
  for (const btn of document.querySelectorAll('[data-cura-filter-toggle]')) {
    btn.setAttribute('aria-expanded', String(open));
  }
  // Only the mobile (<750px) drawer is an overlay, so lock the background scroll
  // there. On desktop the rail is inline (it pushes the grid), so the page must
  // stay scrollable.
  const isOverlay = window.matchMedia('(max-width: 749px)').matches;
  document.body.style.overflow = open && isOverlay ? 'hidden' : '';
}

class CuraCollectionFilters extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-cura-filters-form]');
    this.chipsEl = this.querySelector('[data-cura-applied-chips]');
    this.appliedGroup = this.querySelector('.cura-applied');
    if (!this.form) return;

    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.form.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);

    this.syncChips();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
  }

  onChange(event) {
    // Sort is a single-choice control, not an applied "filter" chip.
    if (event.target.name === 'cura-sort') return;
    this.syncChips();
  }

  onClick(event) {
    // Softness "Why so soft?" tooltip toggle.
    const softnessToggle = event.target.closest('[data-cura-softness-toggle]');
    if (softnessToggle) {
      event.preventDefault();
      this.toggleSoftnessTip(softnessToggle);
      return;
    }
    // Any other click (outside the tooltip) dismisses an open tooltip.
    if (!event.target.closest('[data-cura-softness-tip]')) {
      this.closeSoftnessTip();
    }

    const clear = event.target.closest('[data-cura-filter-clear]');
    if (clear) {
      event.preventDefault();
      this.clearAll();
      return;
    }
    const chip = event.target.closest('[data-chip-for]');
    if (chip) {
      event.preventDefault();
      this.removeChip(chip.dataset.chipFor);
    }
  }

  toggleSoftnessTip(toggle) {
    const tip = toggle.parentElement.querySelector('[data-cura-softness-tip]');
    if (!tip) return;
    const willOpen = tip.hasAttribute('hidden');
    this.closeSoftnessTip();
    if (willOpen) {
      tip.removeAttribute('hidden');
      toggle.setAttribute('aria-expanded', 'true');
    }
  }

  closeSoftnessTip() {
    for (const tip of this.querySelectorAll('[data-cura-softness-tip]:not([hidden])')) {
      tip.setAttribute('hidden', '');
    }
    for (const toggle of this.querySelectorAll('[data-cura-softness-toggle][aria-expanded="true"]')) {
      toggle.setAttribute('aria-expanded', 'false');
    }
  }

  /** Every checked option that counts as an applied filter (everything but sort). */
  selectedInputs() {
    return Array.from(
      this.form.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked')
    ).filter((input) => input.name !== 'cura-sort');
  }

  static keyOf(input) {
    return `${input.name}:${input.value}`;
  }

  labelOf(input) {
    const label = input.closest('label');
    const text = label?.querySelector('.cura-opt__label, .cura-swatch__label');
    return (text?.textContent || input.value).trim();
  }

  syncChips() {
    if (!this.chipsEl) return;
    const selected = this.selectedInputs();
    this.chipsEl.replaceChildren(
      ...selected.map((input) => this.buildChip(this.constructor.keyOf(input), this.labelOf(input)))
    );
    if (this.appliedGroup) this.appliedGroup.hidden = selected.length === 0;
  }

  buildChip(key, label) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'cura-chip';
    chip.dataset.chipFor = key;
    chip.setAttribute('aria-label', `Remove filter: ${label}`);

    const text = document.createElement('span');
    text.className = 'cura-chip__text';
    text.textContent = label;

    const x = document.createElement('span');
    x.className = 'cura-chip__x';
    x.setAttribute('aria-hidden', 'true');
    x.innerHTML =
      '<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M12 2L2 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '<path d="M12 12L2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

    chip.append(text, x);
    return chip;
  }

  removeChip(key) {
    for (const input of this.selectedInputs()) {
      if (this.constructor.keyOf(input) === key) {
        input.checked = false;
        break;
      }
    }
    this.syncChips();
  }

  clearAll() {
    for (const input of this.form.querySelectorAll('input[type="checkbox"]:checked')) {
      input.checked = false;
    }
    // Reset sort to its first (default) option.
    const firstSort = this.form.querySelector('input[name="cura-sort"]');
    if (firstSort) firstSort.checked = true;
    this.syncChips();
  }
}

if (!customElements.get('cura-collection-filters')) {
  customElements.define('cura-collection-filters', CuraCollectionFilters);
}

/* ---- Cross-section toggle (button is in the collection toolbar) ----------- */
if (!window.__curaFiltersToggleInit) {
  window.__curaFiltersToggleInit = true;

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-cura-filter-toggle]')) {
      event.preventDefault();
      setState(!isOpen());
      return;
    }
    if (event.target.closest('[data-cura-filter-close]')) {
      event.preventDefault();
      setState(false);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOpen()) {
      setState(false);
    }
  });

  // Reflect the initial (default) state onto the toggle button(s).
  const initButtons = () => {
    for (const btn of document.querySelectorAll('[data-cura-filter-toggle]')) {
      btn.setAttribute('aria-expanded', String(isOpen()));
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initButtons, { once: true });
  } else {
    initButtons();
  }
}
